import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  RefreshCw,
  HelpCircle,
  Link2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react'
import type {
  CaseEndpoint,
  EndpointRelationship,
  QuestionTemplate,
  UpdateRelationshipRequest,
} from '../../types/case-container'
import {
  CONNECTION_TYPES,
  QUESTION_PREVIEW_LENGTH,
  RELATIONSHIP_STATUSES,
  groupConnectionTypes,
} from './edit-link-modal.constants'

const INPUT_BASE =
  'w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100'

export function ModalHeader({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fuchsia-100 dark:bg-fuchsia-950/50">
          <Link2 className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('infrastructure.editLink.title')}
        </h3>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function DirectionDisplay({
  sourceEndpoint,
  targetEndpoint,
}: {
  sourceEndpoint: CaseEndpoint | undefined
  targetEndpoint: CaseEndpoint | undefined
}): ReactElement {
  const { t } = useTranslation('cases')
  const unknown = t('infrastructure.viewAllLinks.unknown')
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="mb-2 text-xs tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('infrastructure.editLink.direction')}
      </p>
      <div className="flex items-center gap-2 text-sm">
        <span className="rounded bg-fuchsia-100 px-2 py-1 font-medium text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-400">
          {sourceEndpoint?.hostname ?? unknown}
        </span>
        <span className="text-slate-400">&rarr;</span>
        <span className="rounded bg-blue-100 px-2 py-1 font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-400">
          {targetEndpoint?.hostname ?? unknown}
        </span>
      </div>
    </div>
  )
}

export function ErrorBanner({ error }: { error: string | null }): ReactElement | null {
  if (!error) return null
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
    </div>
  )
}

export function ConnectionTypeField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  const groupedTypes = groupConnectionTypes()
  const description = CONNECTION_TYPES.find((type) => type.value === value)?.description
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.editLink.connectionType')}
      </label>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={INPUT_BASE}
      >
        {Object.entries(groupedTypes).map(([category, types]) => (
          <optgroup key={category} label={category}>
            {types.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>
    </div>
  )
}

function statusButtonClass(isActive: boolean, isOpenQuestion: boolean): string {
  if (!isActive) {
    return 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
  }
  if (isOpenQuestion) {
    return 'border-amber-500 bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400'
  }
  return 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/50 text-fuchsia-700 dark:text-fuchsia-400'
}

export function StatusField({
  value,
  onChange,
}: {
  value: string
  onChange: (status: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.editLink.status')}
      </label>
      <div className="flex gap-2">
        {RELATIONSHIP_STATUSES.map((status) => (
          <button
            key={status.value}
            type="button"
            onClick={() => {
              onChange(status.value)
            }}
            className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${statusButtonClass(value === status.value, status.value === 'open-question')}`}
          >
            {status.value === 'open-question' && <HelpCircle className="mr-1 inline h-3.5 w-3.5" />}
            {status.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export function LabelField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.editLink.label')}{' '}
        <span className="font-normal text-slate-400">
          ({t('infrastructure.bulkCreate.optional')})
        </span>
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={`${INPUT_BASE} placeholder:text-slate-400 dark:placeholder:text-slate-500`}
        placeholder={t('infrastructure.editLink.labelPlaceholder')}
      />
    </div>
  )
}

function groupTemplatesByCategory(
  templates: QuestionTemplate[]
): Record<string, QuestionTemplate[]> {
  return templates.reduce<Record<string, QuestionTemplate[]>>((acc, template) => {
    const cat = template.category ?? 'Uncategorised'
    const bucket = acc[cat] ?? []
    bucket.push(template)
    acc[cat] = bucket
    return acc
  }, {})
}

function previewQuestion(text: string): string {
  const truncated = text.slice(0, QUESTION_PREVIEW_LENGTH)
  const suffix = text.length > QUESTION_PREVIEW_LENGTH ? '...' : ''
  return `${truncated}${suffix}`
}

function TemplateSelect({
  templates,
  onSelect,
}: {
  templates: QuestionTemplate[]
  onSelect: (template: QuestionTemplate) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  const grouped = groupTemplatesByCategory(templates)
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-medium text-amber-700 dark:text-amber-400">
        {t('infrastructure.editLink.useTemplate')}
      </label>
      <select
        onChange={(e) => {
          const template = templates.find((tpl) => tpl.id === e.target.value)
          if (template) onSelect(template)
        }}
        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-amber-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('infrastructure.editLink.selectTemplate')}</option>
        {Object.entries(grouped).map(([category, categoryTemplates]) => (
          <optgroup key={category} label={category}>
            {categoryTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {previewQuestion(tpl.questionText)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  )
}

export function OpenQuestionPanel({
  templates,
  formData,
  setFormData,
}: {
  templates: QuestionTemplate[]
  formData: UpdateRelationshipRequest
  setFormData: (data: UpdateRelationshipRequest) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="mb-2 flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
          {t('infrastructure.editLink.openQuestion')}
        </span>
      </div>
      <p className="mb-3 text-xs text-amber-600 dark:text-amber-500">
        {t('infrastructure.editLink.openQuestionDesc')}
      </p>
      {templates.length > 0 && (
        <TemplateSelect
          templates={templates}
          onSelect={(template) => {
            setFormData({ ...formData, investigationNotes: template.questionText })
          }}
        />
      )}
      <textarea
        value={formData.investigationNotes ?? ''}
        onChange={(e) => {
          setFormData({ ...formData, investigationNotes: e.target.value })
        }}
        rows={3}
        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-amber-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.editLink.questionPlaceholder')}
      />
    </div>
  )
}

export function RegularNotesField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.editLink.investigationNotes')}{' '}
        <span className="font-normal text-slate-400">
          ({t('infrastructure.bulkCreate.optional')})
        </span>
      </label>
      <textarea
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        rows={2}
        className={`${INPUT_BASE} placeholder:text-slate-400 dark:placeholder:text-slate-500`}
        placeholder={t('infrastructure.editLink.notesPlaceholder')}
      />
    </div>
  )
}

function EndpointSelect({
  label,
  value,
  currentId,
  endpoints,
  onChange,
}: {
  label: string
  value: string
  currentId: string
  endpoints: CaseEndpoint[]
  onChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  const currentSuffix = ` (${t('infrastructure.editLink.current')})`
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        {endpoints.map((ep) => (
          <option key={ep.id} value={ep.id}>
            {ep.hostname}
            {ep.id === currentId ? currentSuffix : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

interface ChangeEndpointsSectionProps {
  endpoints: CaseEndpoint[]
  relationship: EndpointRelationship
  showChangeEndpoints: boolean
  onToggle: () => void
  newSourceId: string
  setNewSourceId: (id: string) => void
  newTargetId: string
  setNewTargetId: (id: string) => void
  moveError: string | null
  hasEndpointChanges: boolean
  hasSelfLoop: boolean
  moving: boolean
  onMove: () => void
}

function ChangeEndpointsToggle({
  showChangeEndpoints,
  onToggle,
}: {
  showChangeEndpoints: boolean
  onToggle: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
    >
      {showChangeEndpoints ? (
        <ChevronDown className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
      <span className="font-medium">{t('infrastructure.editLink.changeEndpoints')}</span>
      <span className="text-xs text-slate-400">({t('infrastructure.editLink.advanced')})</span>
    </button>
  )
}

function ChangeEndpointsBody(props: ChangeEndpointsSectionProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <div className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>{t('infrastructure.editLink.moveWarning')}</span>
      </div>
      {props.moveError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 dark:border-red-900 dark:bg-red-950/50">
          <p className="text-xs text-red-700 dark:text-red-400">{props.moveError}</p>
        </div>
      )}
      <EndpointSelect
        label={t('infrastructure.editLink.sourceEndpoint')}
        value={props.newSourceId}
        currentId={props.relationship.sourceEndpointId}
        endpoints={props.endpoints}
        onChange={props.setNewSourceId}
      />
      <EndpointSelect
        label={t('infrastructure.editLink.targetEndpoint')}
        value={props.newTargetId}
        currentId={props.relationship.targetEndpointId}
        endpoints={props.endpoints}
        onChange={props.setNewTargetId}
      />
      {props.hasSelfLoop && (
        <p className="text-xs text-red-600 dark:text-red-400">
          {t('infrastructure.editLink.selfLoopError')}
        </p>
      )}
      <button
        type="button"
        onClick={props.onMove}
        disabled={!props.hasEndpointChanges || props.hasSelfLoop || props.moving}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {props.moving && <RefreshCw className="h-4 w-4 animate-spin" />}
        {t('infrastructure.editLink.moveRelationship')}
      </button>
    </div>
  )
}

export function ChangeEndpointsSection(props: ChangeEndpointsSectionProps): ReactElement {
  return (
    <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
      <ChangeEndpointsToggle
        showChangeEndpoints={props.showChangeEndpoints}
        onToggle={props.onToggle}
      />
      {props.showChangeEndpoints && <ChangeEndpointsBody {...props} />}
    </div>
  )
}

export function ModalActions({
  onClose,
  updating,
}: {
  onClose: () => void
  updating: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={updating}
        className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
      >
        {updating && <RefreshCw className="h-4 w-4 animate-spin" />}
        {t('actions.save')}
      </button>
    </div>
  )
}
